import { useQuery, useQueryClient } from '@tanstack/react-query';
import { metadataResponseSchema, type MetadataResponse } from '@castinflow/shared';
import { ApiError } from './api';
import type { Scope } from './scope';

/**
 * Metadata istemci önbelleği (Plan §5 / WP 1.6; WP 2.8 §12):
 * - ETag ile koşullu istek; 304 gelirse eldeki gövde korunur.
 * - Yanıt kullanıcıya projektedir (okunabilir alanlar); anahtar tenant VE kullanıcıyı içerir — aynı tenant'ta başka
 *   kullanıcının metadata'sı asla yeniden kullanılmaz. ETag de sunucuda kullanıcıya özgüdür.
 * - Ayar mutasyonları sonrası `invalidateMetadata()`; çıkış ve kapsam değişiminde bellek tamamen temizlenir.
 */
interface CacheEntry {
  etag: string;
  body: MetadataResponse;
}
const memory = new Map<string, CacheEntry>(); // yalnız bellek; localStorage yok

const memKey = (s: Scope) => `${s.tenantId}:${s.userId}`;

export function clearMetadataMemory(): void {
  memory.clear();
}

export async function fetchMetadata(scope: Scope): Promise<MetadataResponse> {
  const prev = memory.get(memKey(scope));
  const res = await fetch('/api/metadata', {
    credentials: 'same-origin',
    headers: { accept: 'application/json', ...(prev ? { 'if-none-match': prev.etag } : {}) },
  });
  if (res.status === 304 && prev) return prev.body;
  if (!res.ok) {
    throw new ApiError(
      'INTERNAL',
      `HTTP ${res.status}`,
      res.headers.get('x-request-id') ?? '-',
      res.status,
    );
  }
  const body = metadataResponseSchema.parse(await res.json());
  if (body.tenant.id !== scope.tenantId) {
    // Sunucu oturumu başka tenant'a aitse eski anahtar altına yazma
    memory.delete(memKey(scope));
    throw new ApiError('FORBIDDEN', 'Tenant eşleşmiyor', '-', 403);
  }
  const etag = res.headers.get('etag');
  if (etag) memory.set(memKey(scope), { etag, body });
  return body;
}

export function metadataKey(scope: Scope) {
  return ['t', scope.tenantId, 'u', scope.userId, 'metadata'] as const;
}

export function useMetadata(scope: Scope | null) {
  return useQuery({
    queryKey: scope ? metadataKey(scope) : ['metadata', 'none'],
    queryFn: () => fetchMetadata(scope ?? { tenantId: '', userId: '' }),
    enabled: scope !== null,
    staleTime: 60_000,
  });
}

export function useInvalidateMetadata() {
  const qc = useQueryClient();
  return (scope: Scope) => {
    memory.delete(memKey(scope));
    return qc.invalidateQueries({ queryKey: metadataKey(scope) });
  };
}
